import React from 'react';
import {useIntl} from 'react-intl';
import {Alert} from '@forecast-it/design-system';
import {
	WorkDetailOverlap,
	workDetailOverlapLabels,
} from '../../../../components/insights/invoicing/line-generation/invoicing_work_detail_period';

type WorkDetailOverlapNoticeProps = {
	overlaps: ReadonlyArray<WorkDetailOverlap>;
};

/**
 * Warns that the chosen work-detail period re-reports hours an earlier invoice on the same project
 * already showed the client.
 *
 * A warning, never a gate. Overlap is sometimes exactly what the operator wants — re-issuing a
 * corrected invoice covers the same period on purpose — so this changes nothing about whether the
 * invoice can be saved. Nothing about the invoice's amount is affected either way; work detail is
 * uncharged supporting detail.
 *
 * Renders nothing when there is no overlap, so a caller can mount it unconditionally.
 *
 * Alert takes its message through `text` and spreads nothing onto its own wrapper, hence the wrapper
 * here: it is what carries the test and Cypress hooks.
 */
export const WorkDetailOverlapNotice = ({overlaps}: WorkDetailOverlapNoticeProps) => {
	const {formatMessage} = useIntl();

	if (!overlaps || overlaps.length === 0) return null;

	return (
		<div data-testid="work-detail-overlap-notice" data-cy="work-detail-overlap-notice">
			<Alert
				type={Alert.TYPE.WARNING}
				text={formatMessage(
					{id: 'invoicing.work_detail.overlap_warning'},
					{invoices: workDetailOverlapLabels(overlaps)}
				)}
			/>
		</div>
	);
};

export default WorkDetailOverlapNotice;
